import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useId, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type DepartmentRef, type MailRecipientOption, type UserRef } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { FormGrid, FormPage } from '../components/FormPage';
import { PeoplePicker } from '../components/PeoplePicker';
import { Checkbox, Field, Input, Select, useToast } from '../components/ui';

const CODE = /^[A-Z][A-Z0-9_]{1,29}$/;
/** Days of salary to two decimals, 0–30 — what the API accepts for a break penalty. */
const PENALTY_DAYS = /^\d{1,2}(\.\d{1,2})?$/;
const toCode = (value: string) => value.toUpperCase().replace(/[^A-Z0-9_]/g, '_');
const optionalInt = (value: string) => (value.trim() === '' ? null : Math.round(Number(value)));
/** How many people a break type may name to approve it — the API's limit. */
const MAX_APPROVERS = 50;
/** How many employees a break type may be kept for — the API's limit. */
const MAX_EMPLOYEES = 200;
/** How many times a day a break type may allow each employee — the API's limit. */
const MAX_PER_DAY = 50;

/**
 * Who can take a break type: everyone, team leaders and administrators, administrators, the
 * departments picked, or the employees picked.
 */
type Audience = 'everyone' | 'leaders' | 'admins' | 'departments' | 'employees';

/** Routes: `/settings/break-types/new` and `/settings/break-types/:id/edit`. */
export function BreakTypeFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const back = '/settings?tab=attendance';

  const existing = useQuery({
    queryKey: ['attendance', 'break-type', id],
    queryFn: () => api.get<any>(`/attendance/break-types/${id}`),
    enabled: editing,
  });
  // Whom it can name to approve it, or keep it for: every active employee — the list a mail goes to.
  const people = useQuery({
    queryKey: ['mailbox', 'recipients'],
    queryFn: () => api.get<MailRecipientOption[]>('/mailbox/recipients'),
    staleTime: 60_000,
  });
  // Names for those picked who are not on that list: whoever is editing, or someone switched off since.
  const known = useMemo(
    () =>
      new Map<string, UserRef>(
        [...(existing.data?.approvers ?? []), ...(existing.data?.employees ?? [])].map((p: UserRef) => [p.id, p]),
      ),
    [existing.data],
  );
  const approversId = useId();
  const employeesId = useId();
  // The departments it can be kept for: the switched-on ones, and any it is kept for already.
  const departmentOptions = useQuery({
    queryKey: ['departments', 'options'],
    queryFn: () => api.get<DepartmentRef[]>('/departments/options'),
    staleTime: 60_000,
  });
  const departments = useMemo(() => {
    const list = [...(departmentOptions.data ?? [])];
    for (const d of (existing.data?.departments ?? []) as DepartmentRef[]) {
      if (!list.some((x) => x.id === d.id)) list.push(d);
    }
    return list;
  }, [departmentOptions.data, existing.data]);

  const [form, setForm] = useState({
    code: '',
    name: '',
    nameAr: '',
    maxMinutes: '',
    maxPerDay: '',
    penaltyDays: '0.25',
    audience: 'everyone' as Audience,
    departmentIds: [] as string[],
    employeeIds: [] as string[],
    countsAsWork: false,
    requiresApproval: false,
    approverIds: [] as string[],
    sortOrder: '0',
    isActive: true,
  });
  const [loaded, setLoaded] = useState(false);
  if (editing && existing.data && !loaded) {
    const d = existing.data;
    setLoaded(true);
    setForm({
      code: d.code,
      name: d.name,
      nameAr: d.nameAr ?? '',
      maxMinutes: d.maxMinutes == null ? '' : String(d.maxMinutes),
      maxPerDay: d.maxPerDay == null ? '' : String(d.maxPerDay),
      penaltyDays: String(Number(d.penaltyDays ?? 0)),
      audience: d.employees?.length
        ? 'employees'
        : d.departments?.length
          ? 'departments'
          : d.adminsOnly
            ? 'admins'
            : d.leadersOnly
              ? 'leaders'
              : 'everyone',
      departmentIds: (d.departments ?? []).map((x: DepartmentRef) => x.id),
      employeeIds: (d.employees ?? []).map((p: UserRef) => p.id),
      countsAsWork: !!d.countsAsWork,
      requiresApproval: !!d.requiresApproval,
      approverIds: (d.approvers ?? []).map((p: UserRef) => p.id),
      sortOrder: String(d.sortOrder ?? 0),
      isActive: d.isActive,
    });
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: form.code,
        name: form.name.trim(),
        nameAr: form.nameAr.trim() || null,
        maxMinutes: optionalInt(form.maxMinutes),
        // Empty: no limit.
        maxPerDay: optionalInt(form.maxPerDay),
        penaltyDays: Number(form.penaltyDays),
        // One choice: kept for leaders, for administrators, or for the departments or the employees
        // picked — never two from here.
        leadersOnly: form.audience === 'leaders',
        adminsOnly: form.audience === 'admins',
        departmentIds: form.audience === 'departments' ? form.departmentIds : [],
        employeeIds: form.audience === 'employees' ? form.employeeIds : [],
        countsAsWork: form.countsAsWork,
        requiresApproval: form.requiresApproval,
        // Kept while it needs no approval, for when it does again: they decide nothing meanwhile.
        approverIds: form.approverIds,
        sortOrder: optionalInt(form.sortOrder) ?? 0,
        isActive: form.isActive,
      };
      return editing ? api.patch(`/attendance/break-types/${id}`, body) : api.post('/attendance/break-types', body);
    },
    onSuccess: () => {
      toast.success(t('settings.saved'));
      void queryClient.invalidateQueries({ queryKey: ['attendance'] });
      navigate(back);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const minutes = optionalInt(form.maxMinutes);
  const perDay = optionalInt(form.maxPerDay);
  const perDayValid = perDay === null || (Number.isInteger(perDay) && perDay >= 1 && perDay <= MAX_PER_DAY);
  const penaltyValid = PENALTY_DAYS.test(form.penaltyDays.trim()) && Number(form.penaltyDays) <= 30;
  const missingDepartments = form.audience === 'departments' && form.departmentIds.length === 0;
  const missingEmployees = form.audience === 'employees' && form.employeeIds.length === 0;
  const valid =
    CODE.test(form.code) &&
    !!form.name.trim() &&
    (minutes === null || minutes > 0) &&
    perDayValid &&
    penaltyValid &&
    !missingDepartments &&
    !missingEmployees;

  return (
    <FormPage
      title={editing ? t('settings.editBreakType') : t('settings.newBreakType')}
      subtitle={t('settings.breakTypesNote')}
      backTo={back}
      backLabel={t('settings.attendance')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={!valid}
      loading={editing && existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={`${t('common.name')} (EN)`} required>
          <Input value={form.name} maxLength={60} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
        </Field>
        <Field label={`${t('common.name')} (AR)`}>
          <Input dir="rtl" value={form.nameAr} maxLength={60} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} />
        </Field>
        <Field label={t('common.code')} required hint={t('settings.codeHint')}>
          <Input dir="ltr" value={form.code} maxLength={30} onChange={(e) => setForm({ ...form, code: toCode(e.target.value) })} />
        </Field>
        <Field label={t('settings.allowanceMinutes')} hint={t('settings.allowanceHint')}>
          <Input
            type="number"
            min={1}
            max={600}
            value={form.maxMinutes}
            onChange={(e) => setForm({ ...form, maxMinutes: e.target.value })}
          />
        </Field>
        <Field
          label={t('settings.overrunPenalty')}
          hint={t('settings.overrunPenaltyHint')}
          error={penaltyValid ? undefined : t('settings.overrunPenaltyInvalid')}
        >
          <Input
            type="number"
            min={0}
            max={30}
            step={0.25}
            value={form.penaltyDays}
            onChange={(e) => setForm({ ...form, penaltyDays: e.target.value })}
          />
        </Field>
        {/* How many times a day each employee may take it (operator, 2026-10-28) — under the allowance,
            the other limit. Order moved to the end to make room, so every row still pairs up. */}
        <Field
          label={t('settings.maxPerDay')}
          hint={t('settings.maxPerDayHint')}
          error={perDayValid ? undefined : t('settings.maxPerDayInvalid', { n: MAX_PER_DAY })}
        >
          <Input
            type="number"
            min={1}
            max={MAX_PER_DAY}
            value={form.maxPerDay}
            onChange={(e) => setForm({ ...form, maxPerDay: e.target.value })}
          />
        </Field>
        {/* Kept for leaders, only attendance.leader_breaks (team leaders, administrators) is offered it;
            for administrators (operator, 2026-10-19), only attendance.admin_breaks (the Administrator role);
            for departments (operator, 2026-10-19), only their people; for employees (operator,
            2026-10-24), only the people picked. */}
        <Field label={t('settings.breakTakenBy')} hint={t('settings.breakTakenByHint')}>
          <Select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value as Audience })}>
            <option value="everyone">{t('settings.breakForEveryone')}</option>
            <option value="leaders">{t('settings.breakForLeaders')}</option>
            <option value="admins">{t('settings.breakForAdmins')}</option>
            <option value="departments">{t('settings.breakForDepartments')}</option>
            <option value="employees">{t('settings.breakForEmployees')}</option>
          </Select>
        </Field>
        {/* Ticked (a meeting), the break stays in Worked; unticked, it comes off it. */}
        <Field label={t('settings.countsAsWork')} hint={t('settings.countsAsWorkHint')}>
          <Checkbox
            checked={form.countsAsWork}
            onChange={(e) => setForm({ ...form, countsAsWork: e.target.checked })}
            label={form.countsAsWork ? t('settings.countsAsWorkOn') : t('settings.countsAsWorkOff')}
          />
        </Field>
        {/* Under the row with Who can take it, so the two fields beside each other stay together. */}
        {form.audience === 'departments' && (
          <fieldset className="sm:col-span-2">
            <legend className="mb-1.5 text-body-sm font-medium text-[var(--text-secondary)]">{t('settings.breakDepartments')}</legend>
            <div className={clsx('rounded-md border p-3', missingDepartments ? 'border-warning/35' : 'hairline')}>
              {!departmentOptions.data && !existing.data?.departments?.length ? (
                <p className="text-caption text-muted">{t('common.loading')}</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {departments.map((d) => (
                    <Checkbox
                      key={d.id}
                      label={
                        <>
                          <bdi>{localized(d)}</bdi>
                          {d.isActive === false && <span className="text-faint"> · {t('settings.switchedOff')}</span>}
                        </>
                      }
                      checked={form.departmentIds.includes(d.id)}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          departmentIds: e.target.checked
                            ? [...form.departmentIds, d.id]
                            : form.departmentIds.filter((x) => x !== d.id),
                        })
                      }
                    />
                  ))}
                </div>
              )}
            </div>
            {missingDepartments ? (
              <p className="mt-1 text-caption text-warning-fg dark:text-warning-dark">{t('settings.breakPickDepartments')}</p>
            ) : (
              <p className="mt-1 text-caption text-faint">{t('settings.breakDepartmentsHint')}</p>
            )}
          </fieldset>
        )}
        {/* The employees it is kept for, in the same place. Not a Field: that is a <label>, and a
            label around the chips' buttons would press them. */}
        {form.audience === 'employees' && (
          <div className="sm:col-span-2">
            <label htmlFor={employeesId} className="mb-1.5 block text-body-sm font-medium text-[var(--text-secondary)]">
              {t('settings.breakEmployees')}
            </label>
            <PeoplePicker
              inputId={employeesId}
              people={people.data}
              value={form.employeeIds}
              onChange={(employeeIds) => setForm({ ...form, employeeIds })}
              max={MAX_EMPLOYEES}
              known={known}
              invalid={missingEmployees}
              fullLabel={t('settings.breakEmployeesMax', { n: MAX_EMPLOYEES })}
            />
            {missingEmployees ? (
              <p className="mt-1 text-caption text-warning-fg dark:text-warning-dark">{t('settings.breakPickEmployees')}</p>
            ) : (
              <p className="mt-1 text-caption text-faint">{t('settings.breakEmployeesHint')}</p>
            )}
          </div>
        )}
        {/* Ticked, pressing it asks the administrators (attendance.breaks.approve) and the people
            picked below, and it starts once one of them approves. */}
        <Field label={t('settings.needsApprovalLabel')} hint={t('settings.needsApprovalHint')}>
          <Checkbox
            checked={form.requiresApproval}
            onChange={(e) => setForm({ ...form, requiresApproval: e.target.checked })}
            label={form.requiresApproval ? t('settings.needsApprovalOn') : t('settings.needsApprovalOff')}
          />
        </Field>
        <Field label={t('common.status')} hint={t('settings.offeredHint')}>
          <Checkbox
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            label={form.isActive ? t('settings.offered') : t('settings.switchedOff')}
          />
        </Field>
        {/* Who approves it beside the administrators (operator, 2026-10-19). Not a Field: that is a
            <label>, and a label around the chips' buttons would press them. */}
        {form.requiresApproval && (
          <div className="sm:col-span-2">
            <label htmlFor={approversId} className="mb-1.5 block text-body-sm font-medium text-[var(--text-secondary)]">
              {t('settings.breakApprovers')}
            </label>
            <PeoplePicker
              inputId={approversId}
              people={people.data}
              value={form.approverIds}
              onChange={(approverIds) => setForm({ ...form, approverIds })}
              max={MAX_APPROVERS}
              known={known}
              fullLabel={t('settings.breakApproversMax', { n: MAX_APPROVERS })}
            />
            <span className="mt-1 block text-caption text-faint">{t('settings.breakApproversHint')}</span>
          </div>
        )}
        <Field label={t('settings.order')} hint={t('settings.orderHint')}>
          <Input type="number" min={0} max={999} value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/** Routes: `/settings/leave-types/new` and `/settings/leave-types/:id/edit`. */
export function LeaveTypeFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const back = '/settings?tab=leave';

  const existing = useQuery({
    queryKey: ['leaves', 'type', id],
    queryFn: () => api.get<any>(`/leaves/types/${id}`),
    enabled: editing,
  });

  const [form, setForm] = useState({
    code: '',
    name: '',
    nameAr: '',
    isPaid: true,
    annualAllowanceDays: '',
    sortOrder: '0',
    isActive: true,
  });
  const [loaded, setLoaded] = useState(false);
  if (editing && existing.data && !loaded) {
    const d = existing.data;
    setLoaded(true);
    setForm({
      code: d.code,
      name: d.name,
      nameAr: d.nameAr ?? '',
      isPaid: d.isPaid,
      annualAllowanceDays: d.annualAllowanceDays == null ? '' : String(d.annualAllowanceDays),
      sortOrder: String(d.sortOrder ?? 0),
      isActive: d.isActive,
    });
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: form.code,
        name: form.name.trim(),
        nameAr: form.nameAr.trim() || null,
        isPaid: form.isPaid,
        annualAllowanceDays: optionalInt(form.annualAllowanceDays),
        sortOrder: optionalInt(form.sortOrder) ?? 0,
        isActive: form.isActive,
      };
      return editing ? api.patch(`/leaves/types/${id}`, body) : api.post('/leaves/types', body);
    },
    onSuccess: () => {
      toast.success(t('settings.saved'));
      void queryClient.invalidateQueries({ queryKey: ['leaves'] });
      navigate(back);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const allowance = optionalInt(form.annualAllowanceDays);
  const valid = CODE.test(form.code) && !!form.name.trim() && (allowance === null || allowance >= 0);

  return (
    <FormPage
      title={editing ? t('settings.editLeaveType') : t('settings.newLeaveType')}
      subtitle={t('settings.leaveTypesNote')}
      backTo={back}
      backLabel={t('settings.leave')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={!valid}
      loading={editing && existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={`${t('common.name')} (EN)`} required>
          <Input value={form.name} maxLength={60} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
        </Field>
        <Field label={`${t('common.name')} (AR)`}>
          <Input dir="rtl" value={form.nameAr} maxLength={60} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} />
        </Field>
        <Field label={t('common.code')} required hint={t('settings.codeHint')}>
          <Input dir="ltr" value={form.code} maxLength={30} onChange={(e) => setForm({ ...form, code: toCode(e.target.value) })} />
        </Field>
        <Field label={t('settings.annualAllowance')} hint={t('settings.annualAllowanceHint')}>
          <Input
            type="number"
            min={0}
            max={366}
            value={form.annualAllowanceDays}
            onChange={(e) => setForm({ ...form, annualAllowanceDays: e.target.value })}
          />
        </Field>
        <Field label={t('settings.isPaid')}>
          <Checkbox
            checked={form.isPaid}
            onChange={(e) => setForm({ ...form, isPaid: e.target.checked })}
            label={form.isPaid ? t('leaves.paid') : t('leaves.unpaid')}
          />
        </Field>
        <Field label={t('settings.order')} hint={t('settings.orderHint')}>
          <Input type="number" min={0} max={999} value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
        </Field>
        <Field label={t('common.status')} hint={t('settings.offeredHint')}>
          <Checkbox
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            label={form.isActive ? t('settings.offered') : t('settings.switchedOff')}
          />
        </Field>
      </FormGrid>
    </FormPage>
  );
}
